import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, Check, CheckCheck } from 'lucide-react';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Bell icon with unread-count dot. Opens a small tray listing the latest
 * notifications, click a row → deep-link URL, one-click "Read All".
 * Polls every 30s so a new statement upload shows up promptly.
 */
export default function NotificationBell({ onNavigate }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/user-notifications`, { headers, params: { limit: 20 } });
      setItems(res.data.items || []);
      setUnread(res.data.unread_count || 0);
    } catch { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/user-notifications/${id}/read`, {}, { headers });
      await load();
    } catch { /* silent */ }
  };

  const readAll = async () => {
    try {
      const res = await axios.post(`${API}/user-notifications/read-all`, {}, { headers });
      if (res.data.marked_read > 0) toast.success(`Marked ${res.data.marked_read} as read`);
      await load();
    } catch { toast.error('Could not mark all read'); }
  };

  const openItem = async (n) => {
    if (!n.read_at) await markRead(n.id);
    setOpen(false);
    if (n.url && onNavigate) onNavigate(n.url);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative h-9 w-9 p-0" data-testid="notif-bell">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span className="absolute top-0 right-0 min-w-[16px] h-4 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center px-1" data-testid="notif-unread-dot">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <span className="text-sm font-semibold">Notifications</span>
          {unread > 0 && (
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={readAll} data-testid="notif-read-all">
              <CheckCheck className="h-3 w-3 mr-1" /> Read All
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-6 text-center text-xs text-muted-foreground">No notifications yet.</div>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                onClick={() => openItem(n)}
                className={`w-full text-left px-3 py-2 border-b last:border-b-0 hover:bg-slate-50 transition-colors ${!n.read_at ? 'bg-amber-50/60' : ''}`}
                data-testid={`notif-item-${n.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{n.title}</div>
                    {n.message && <div className="text-xs text-muted-foreground line-clamp-2">{n.message}</div>}
                    <div className="text-[10px] text-muted-foreground mt-1">
                      {new Date(n.created_at).toLocaleString()}
                    </div>
                  </div>
                  {!n.read_at && (
                    <span className="mt-1 h-2 w-2 rounded-full bg-rose-600 shrink-0" title="Unread" />
                  )}
                  {n.read_at && <Check className="h-3 w-3 text-emerald-600 mt-1 shrink-0" />}
                </div>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
